import { useAtomValue } from 'jotai'
import React from 'react'
import * as R from 'remeda'

import { type ColumnInfo, exists, type RowInfo } from '~/utils'

import { columnWidthMapAtom } from '../atoms'

import { Cell } from './Cell'
import styles from './TableContent.css'

const calcOffsets = (
  lengthMap: Record<number, number>,
  numberOfOffset: number,
) =>
  R.range(0, numberOfOffset).map((i) =>
    R.range(0, i).reduce<number | undefined>(
      (prev = 0, j) => (exists(lengthMap[j]) ? prev + lengthMap[j] : undefined),
      0,
    ),
  )

export type TableContentProps<ColumnKey extends string> = {
  columns: ColumnInfo<ColumnKey>[]
  'data-testid'?: string
  disabled?: boolean
  stickyColumns?: number
  tableInfo: RowInfo<ColumnKey>[]
  textLineCount?: number
}

export const TableContent = React.forwardRef(
  <ColumnKey extends string>(
    {
      columns,
      'data-testid': dataTestId = 'components-features-table-components-table-content',
      disabled,
      stickyColumns = 0,
      tableInfo,
      textLineCount = 1,
    }: TableContentProps<ColumnKey>,
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const columnWidthMap = useAtomValue(columnWidthMapAtom)

    const gridTemplateColumns = React.useMemo(
      () => columns.map(R.prop('width')).join(' '),
      [columns],
    )

    const lefts = React.useMemo(
      () => calcOffsets(columnWidthMap, stickyColumns),
      [columnWidthMap, stickyColumns],
    )

    return (
      <div
        ref={ref}
        className={styles.table}
        data-testid={dataTestId}
        style={{ gridTemplateColumns }}
      >
        {tableInfo.flatMap((rowInfo, rowIndex) =>
          columns.map(({ key }, columnIndex) => {
            const cellInfo = rowInfo[key]
            if (cellInfo === undefined) {
              return null
            }

            return (
              <Cell
                key={`${rowIndex}-${columnIndex}`}
                cellInfo={cellInfo}
                columnIndex={columnIndex}
                disabled={disabled}
                isAfterSpacerColumn={
                  columns[columnIndex - 1]?.isSpacer === true
                }
                left={lefts[columnIndex]}
                rowIndex={rowIndex}
                textLineCount={textLineCount}
              />
            )
          }),
        )}
      </div>
    )
  },
)
